/**
 * FreshStartFlow — Door 1 of /switch-from-quickbooks.
 *
 * The simplest path: user picks a date, we stamp `qb_cutover_date` on
 * their workspace, and they're done. No exports, no spreadsheets.
 * Bank sync (Plaid) + OCR scans + manual entry handle everything from
 * cutover_date forward.
 *
 * Best for: most small businesses. The 90% case.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../fetchInterceptor';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  Calendar, Check, Loader2, Landmark, ScanLine, ArrowRight,
  CheckCircle2, Sparkles, ArrowLeft,
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function lastDayOfPriorMonth() {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10);
}

export default function FreshStartFlow({ workspaceId, onBack }) {
  const [cutoverDate, setCutoverDate] = useState(lastDayOfPriorMonth());
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const commit = async () => {
    setBusy(true);
    try {
      const r = await apiFetch(`${API_URL}/api/migration/fresh-start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: workspaceId, cutover_date: cutoverDate }),
      });
      const d = await r.json();
      if (!r.ok) {
        toast.error(d.detail || 'Could not set cutover date');
        return;
      }
      setDone(true);
      toast.success('You\'re live on CountBucks!');
    } catch (e) {
      toast.error(e.message || 'Network error');
    }
    setBusy(false);
  };

  if (done) {
    return (
      <Card data-testid="fresh-start-done">
        <CardContent className="p-8 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-500" />
          </div>
          <div>
            <h3 className="text-2xl font-bold">You're live on CountBucks 🎉</h3>
            <p className="text-sm text-muted-foreground mt-1">
              CountBucks is now your source of truth starting <span className="font-mono font-semibold text-foreground">{cutoverDate}</span>.
              Here's how to start bringing your money in:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-2xl mx-auto pt-2 text-left">
            <Link
              to="/banking"
              className="rounded-xl border bg-card p-4 hover:border-emerald-500/40 hover:shadow-sm transition-all"
              data-testid="fresh-start-next-banking"
            >
              <div className="flex items-center gap-2">
                <Landmark className="w-4 h-4 text-emerald-500" />
                <span className="font-semibold text-sm">Connect your bank (Plaid)</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Auto-pulls every transaction from {cutoverDate} onward. ~2 minutes.
              </p>
              <span className="inline-flex items-center text-xs text-emerald-600 mt-2 font-medium">
                Connect now <ArrowRight className="w-3 h-3 ml-1" />
              </span>
            </Link>
            <Link
              to="/documents"
              className="rounded-xl border bg-card p-4 hover:border-emerald-500/40 hover:shadow-sm transition-all"
              data-testid="fresh-start-next-documents"
            >
              <div className="flex items-center gap-2">
                <ScanLine className="w-4 h-4 text-emerald-500" />
                <span className="font-semibold text-sm">Scan receipts &amp; bills</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Drop PDFs or photos — AI categorizes and posts the JE for you.
              </p>
              <span className="inline-flex items-center text-xs text-emerald-600 mt-2 font-medium">
                Open Documents <ArrowRight className="w-3 h-3 ml-1" />
              </span>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="fresh-start-flow">
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-emerald-500" />
              Fresh Start
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Pick a date to start fresh. CountBucks becomes your source of truth from that day forward.
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onBack} data-testid="fresh-start-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label className="text-sm font-medium flex items-center gap-1.5">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            Your cutover date
          </Label>
          <Input
            type="date"
            value={cutoverDate}
            onChange={(e) => setCutoverDate(e.target.value)}
            className="max-w-xs"
            data-testid="fresh-start-date"
          />
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Most common picks:</strong> the last day of last month, or January 1st of this year.
          </p>
        </div>

        <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
          <p className="text-sm font-medium">What happens next:</p>
          <ol className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 font-bold tabular-nums w-5 shrink-0">1.</span>
              <span>We stamp <span className="font-mono text-foreground">{cutoverDate}</span> as your "live in CountBucks" date — no opening balances posted, nothing imported from QB.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 font-bold tabular-nums w-5 shrink-0">2.</span>
              <span>You'll connect your bank account so transactions from this date forward auto-flow in.</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 font-bold tabular-nums w-5 shrink-0">3.</span>
              <span>Optionally drop in receipts/bills — AI categorizes and posts the JE automatically.</span>
            </li>
          </ol>
        </div>

        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
          <strong>Heads up:</strong> historical periods (before {cutoverDate}) will be empty in CountBucks. If you need
          full historical P&amp;L or Balance Sheet to show in reports, pick "Match my QuickBooks reports" instead.
        </div>

        <Button
          onClick={commit}
          disabled={busy || !cutoverDate}
          className="bg-emerald-600 hover:bg-emerald-500 text-white"
          data-testid="fresh-start-commit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          Set cutover date &amp; finish
        </Button>
      </CardContent>
    </Card>
  );
}
